import { useFocusEffect, useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import firebase from "firebase";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { v4 as uuidv4 } from "uuid";

import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdInput, tPdInputPdfValue } from "src/components/forms/input";
import {
  PdHookFormCheckboxGroup,
  PdHookFormErrorMessage,
  PdHookFormImagePicker,
  PdHookFormInput,
  PdHookFormInputFloat,
  PdHookFormInputPdf,
  PdHookFormInputPercentage,
  PdHookFormInputPhone,
  PdHookFormSelect,
  PdHookFormSelectMultiple,
  PdHookFormSwitch,
} from "src/components/hook-form";
import { PdStack, PdText } from "src/components/layout";
import { PdDivider } from "src/components/layout/table";
import { documentBrand } from "src/db/brand.db";
import { queryUser } from "src/db/user.db";
import { useChangeEmailModal, useVendorSetupAccount } from "src/hooks/modals";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kRoute } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { updateBrandProfile } from "src/redux/company/actions";
import {
  brandCategoryOptions,
  brandEthosOptions,
  countriesOptions,
  pressPublicationOptions,
  shippingLeadTimeOptions,
  tCountryOption,
  usStateOptions,
} from "src/util/comboBoxData";
import { mapEntityArrayToFormOptions, mapFormOptionsToEntityArray } from "src/util/form";
import { getSafeUrl } from "src/util/functions";
import { formatPercentNumber } from "src/util/gFormatting";
import { getDefaultAddress } from "src/util/user";
import {
  tBrand,
  tBrandCategory,
  tBrandEthos,
  tBrandPressPublication,
  tHeroImage,
  tUser,
  tVendorData,
} from "tmt_api/src/types/types";
import { PdFormCard, PdSectionBadge, PdSectionBadgeLabel } from "./P09_Profile/components";
import { PdHookFormMultipleImagePicker, tImagePickerValue } from "./P09_Profile/hook-form";
import { useSelector } from "react-redux";
import { openUrlInNewTab } from "src/util/linking";
import { PdTruck } from "src/components/Icons";

type tAdminBrandSettingsParams = {
  params: { brandId?: string };
  key: string;
  name: string;
};

type tOptionFormData<T extends unknown = string> = { label: string; value: T } | null;

type tBrandFormData = {
  businessName: string;
  name: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: { value: string; label: string } | null;
  zipCode: string;
  country: tCountryOption | null;
  category: tOptionFormData<tBrandCategory>;
  taxInfo: tPdInputPdfValue;
  taxId: string;
  instagram: string;
  facebook: string;
  website: string;
  videoUrl: string;
  aboutMe: string;
  brandEthos: tBrandEthos;
  cityBased: string;
  openingMinimum: string;
  reorderMinimum: string;
  shippingLeadTime: tOptionFormData<string>;
  defaultShipPercent: number | null;
  assetsFolderUrl: string;
  avatar: tImagePickerValue | null;
  carousel: tImagePickerValue[];
  pressPublications: tOptionFormData<tBrandPressPublication>[];
  isExclusive: boolean;
  isPublished: boolean;
  commissionFee: number | null;
  serviceFee: number | null;
};

const AdminBrandForm: React.FC<{ brand: tBrand; user: tUser }> = ({ brand, user }) => {
  const address = getDefaultAddress(brand.addresses);
  const isMobile = useIsMobile();
  const navigation = useNavigation();
  const rTermsAndConditionsSampleUrl = useSelector((state) => state.ConfigsReducer.data.urls?.termsAndConditionsSample);
  const rEstimatingShippingUrl = useSelector((state) => state.ConfigsReducer.data.urls?.estimatingShipping);
  const dispatch = useAppDispatch();
  const vendorSetupAccount = useVendorSetupAccount();
  const changeEmailModal = useChangeEmailModal();
  const formMethods = useForm<tBrandFormData>({
    defaultValues: {
      businessName: brand.name || "",
      name: brand.contactPerson || "",
      phone: brand.contacts[0]?.phone ?? "",
      addressLine1: address.address1,
      addressLine2: address.address2,
      city: address.city,
      state: address.state_or_province
        ? usStateOptions.find((state) => [state.label, state.value].includes(address.state_or_province)) ?? null
        : null,
      zipCode: address.postal_code || "",
      country: address.country_code
        ? countriesOptions.find((country) => country.value === address.country_code) ?? null
        : null,
      category: brand.primaryCategory
        ? brandCategoryOptions.find((category) => category.value === brand.primaryCategory) ?? null
        : null,
      taxInfo: brand.termsAndConditions
        ? { uri: brand.termsAndConditions.url, name: brand.termsAndConditions.name }
        : null,
      taxId: brand.taxId || "",
      instagram: brand.links?.instagram ?? "",
      facebook: brand.links?.facebook ?? "",
      website: brand.website || "",
      videoUrl: brand.videoUrl || "",
      aboutMe: brand.brandStory || "",
      brandEthos: brand.brandEthos || [],
      cityBased: brand.cityBased || "",
      openingMinimum: brand.openingMin ? `${brand.openingMin}` : "",
      reorderMinimum: brand.reorderMin ? `${brand.reorderMin}` : "",
      shippingLeadTime: brand.shippingLeadTime
        ? shippingLeadTimeOptions.find((leadTime) => leadTime.value === brand.shippingLeadTime) ?? null
        : shippingLeadTimeOptions[0],
      defaultShipPercent: brand.defaultShipPercent ? formatPercentNumber(brand.defaultShipPercent) : null,
      assetsFolderUrl: brand.assetsFolderUrl || "",
      avatar: brand.images.logoDownloadURL ? { uri: brand.images.logoDownloadURL } : null,
      carousel: brand.images.heroes?.map((hero) => ({ uri: hero.downloadURL })) ?? [],
      pressPublications: mapEntityArrayToFormOptions(brand.pressPublications, pressPublicationOptions),
      isExclusive: brand.isExclusive,
      isPublished: brand.pauseMode !== "pause",
      commissionFee: brand.commissionFee ? formatPercentNumber(brand.commissionFee) : null,
      serviceFee: brand.serviceFee ? formatPercentNumber(brand.serviceFee) : null,
    },
  });
  const [isBankConnected, setBankConnected] = React.useState(!!brand.vendorId);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleOnPressSetupAccount = async () => {
    const defaultValue: tVendorData = {
      businessName: brand.name ?? "",
      businessDomain: "",
      ein: brand.taxId ?? "",
      url: "",
      emailAddress: brand.email ?? "",
      address1: address.address1,
      address2: address.address2 ?? "",
      city: address.city,
      state: address.state_or_province,
      zipCode: address.postal_code,
      phone: brand.contacts[0]?.phone ?? "",
      country: address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    const response = await vendorSetupAccount.openAsync({ mode: "brand", defaultValue, isEditing: isBankConnected });
    if (response.status === "closed") return;
    setBankConnected(true);
  };

  const handleOnPressChangeEmail = () => {
    changeEmailModal.openAsync({ accountType: "brand", user });
  };

  const handleOnPressViewMyStore = () => {
    navigation.navigate(kRoute.P100, { brand: brand.name });
  };

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);
      const payload: tBrand = {
        ...brand,
        name: formData.businessName,
        contactPerson: formData.name,
        contacts: [{ ...brand.contacts[0], phone: formData.phone }],
        addresses: [
          {
            ...brand.addresses[0],
            address1: formData.addressLine1 ?? "",
            address2: formData.addressLine2,
            city: formData.city,
            state_or_province: formData.state?.value ?? "",
            postal_code: formData.zipCode ?? "",
            country_code: formData.country?.value ?? brand.addresses[0].country_code,
          },
        ],
        primaryCategory: formData.category?.value,
        taxId: formData.taxId,
        links: {
          instagram: getSafeUrl(formData.instagram),
          facebook: getSafeUrl(formData.facebook),
        },
        website: formData.website,
        videoUrl: formData.videoUrl,
        brandStory: formData.aboutMe,
        brandEthos: formData.brandEthos,
        cityBased: formData.cityBased,
        openingMin: formData.openingMinimum ? parseFloat(formData.openingMinimum) : 0,
        reorderMin: formData.reorderMinimum ? parseFloat(formData.reorderMinimum) : 0,
        shippingLeadTime: formData.shippingLeadTime?.value,
        defaultShipPercent: (formData.defaultShipPercent || 0) / 100,
        assetsFolderUrl: formData.assetsFolderUrl,
        pressPublications: mapFormOptionsToEntityArray(formData.pressPublications),
        isExclusive: formData.isExclusive,
        pauseMode: formData.isPublished ? "" : "pause",
        commissionFee: (formData.commissionFee || 0) / 100,
        serviceFee: (formData.serviceFee || 0) / 100,
      };

      if (formData.taxInfo?.file) {
        const snapshot = await firebase
          .storage()
          .ref(`brands/${brand.id}/uploads/terms_and_conditions.pdf`)
          .put(formData.taxInfo.file);
        const url: string = await snapshot.ref.getDownloadURL();
        payload.termsAndConditions = { name: formData.taxInfo?.name ?? "", url };
      }

      if (formData.avatar?.file) {
        const fileExtension = formData.avatar.file.type.replace(/^.*\//gm, "");
        const snapshot = await firebase
          .storage()
          .ref(`rep/${brand.id}/uploads/profile.${fileExtension}`)
          .put(formData.avatar.file);
        payload.images.logoDownloadURL = await snapshot.ref.getDownloadURL();
      }

      const carouselImageUrls: tHeroImage[] = [];
      for (const carouselImage of formData.carousel) {
        if (carouselImage.uri?.startsWith("https://")) {
          carouselImageUrls.push({ downloadURL: carouselImage.uri, isPrimary: false });
          continue;
        }
        if (!carouselImage.file) continue;
        const fileExtension = carouselImage.file.type.replace(/^.*\//gm, "");
        const snapshot = await firebase
          .storage()
          .ref(`brands/${brand.id}/uploads/hero${uuidv4()}.${fileExtension}`)
          .put(carouselImage.file);
        const url = await snapshot.ref.getDownloadURL();
        carouselImageUrls.push({ downloadURL: url, isPrimary: false });
      }
      payload.images.heroes = carouselImageUrls;

      await dispatch(updateBrandProfile(payload, user));
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <FormProvider {...formMethods}>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[1, 1]}
        gap={3}
        style={[styles.formSection, styles.zIndex1]}
      >
        <PdFormCard>
          <View style={styles.formSectionTitle}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>1</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              The Basics
            </PdText>
          </View>
          <PdDivider style={styles.formSectionDivider} />
          <View style={styles.mb3}>
            <PdFormLabel>Company Name *</PdFormLabel>
            <PdHookFormInput name="businessName" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="businessName" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Contact Full Name *</PdFormLabel>
            <PdHookFormInput name="name" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="name" />
          </View>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formSection}>
            <View>
              <PdFormLabel>Primary Phone Number *</PdFormLabel>
              <PdHookFormInputPhone
                name="phone"
                placeholder="(XXX) XXX-XXXX"
                autoCompleteType="tel"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="phone" />
            </View>
            <View>
              <PdFormLabel>Primary Email</PdFormLabel>
              <PdInput isDisabled value={user.email} />
            </View>
          </PdStack>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Adress Line 1 *</PdFormLabel>
            <PdHookFormInput name="addressLine1" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="addressLine1" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Adress Line 2</PdFormLabel>
            <PdHookFormInput name="addressLine2" onSubmitEditing={handleOnSubmit} />
          </View>
          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={[styles.selectFormSection, styles.zIndex2]}
          >
            <View>
              <PdFormLabel>Primary City *</PdFormLabel>
              <PdHookFormInput name="city" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="city" />
            </View>
            <View>
              <PdFormLabel>State *</PdFormLabel>
              <PdHookFormSelect
                name="state"
                rules={{ required: "Required" }}
                options={usStateOptions}
                isClearable={false}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="state" />
            </View>
          </PdStack>
          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={[styles.selectFormSection, styles.zIndex1]}
          >
            <View>
              <PdFormLabel>Zip *</PdFormLabel>
              <PdHookFormInput name="zipCode" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="zipCode" />
            </View>
            <View>
              <PdFormLabel>Country *</PdFormLabel>
              <PdHookFormSelect
                name="country"
                rules={{ required: "Required" }}
                placeholder="Write your country..."
                options={countriesOptions}
                isClearable={false}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="country" />
            </View>
          </PdStack>
          <View>
            <PdFormLabel>Category *</PdFormLabel>
            <PdHookFormSelect
              name="category"
              rules={{ required: "Required" }}
              placeholder="E.g. Gift Shop"
              options={brandCategoryOptions.sort((c1, c2) => c1.label.localeCompare(c2.label))}
              isClearable={false}
              onSubmitEditing={handleOnSubmit}
            />
            <PdHookFormErrorMessage name="category" />
          </View>
        </PdFormCard>
        <PdFormCard>
          <View style={styles.formSectionTitle}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>2</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              Admin
            </PdText>
          </View>
          <PdDivider style={styles.formSectionDivider} />
          <View style={styles.mb3}>
            <View style={styles.formLabelLinkContainer}>
              <PdFormLabel>Upload your Terms & Conditions</PdFormLabel>
              {rTermsAndConditionsSampleUrl && (
                <TouchableOpacity onPress={() => openUrlInNewTab(rTermsAndConditionsSampleUrl)}>
                  <PdText style={styles.formLabelLinkLabel}>
                    {isMobile ? "View Sample" : "View Sample Terms & Conditions"}
                  </PdText>
                </TouchableOpacity>
              )}
            </View>
            <PdHookFormInputPdf name="taxInfo" isMobile={isMobile} />
            <PdHookFormErrorMessage name="taxInfo" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Company Tax ID</PdFormLabel>
            <PdHookFormInput name="taxId" />
            <PdHookFormErrorMessage name="taxId" />
          </View>
          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[1, 1]}
            gap={3}
            style={styles.selectFormSection}
          >
            <View>
              <PdFormLabel>{isBankConnected ? "Update your Bank Account" : "Connect your Bank Account"}</PdFormLabel>
              <PdButton
                size="sm"
                colorScheme="pink"
                onPress={handleOnPressSetupAccount}
                isFullWidth={isMobile}
                style={styles.mrAuto}
              >
                <PdButtonLabel>{isBankConnected ? "Update" : "Connect"}</PdButtonLabel>
              </PdButton>
            </View>
            {/* TODO: Add Automate Connection Requests */}
          </PdStack>
        </PdFormCard>
      </PdStack>
      <PdFormCard style={styles.formSection}>
        <View style={styles.formSectionTitle}>
          <PdSectionBadge>
            <PdSectionBadgeLabel>3</PdSectionBadgeLabel>
          </PdSectionBadge>
          <PdText variant="fieldset" style={styles.ml2}>
            My Profile
          </PdText>
        </View>
        <PdDivider style={styles.formSectionDivider} />
        <PdStack direction={isMobile ? "vertical" : "horizontal"} gap={3}>
          <View style={isMobile ? {} : styles.flex1}>
            <View style={styles.mb3}>
              <PdFormLabel>Instagram</PdFormLabel>
              <PdHookFormInput
                name="instagram"
                placeholder="ex: www.instagram.com/brandname"
                onSubmitEditing={handleOnSubmit}
              />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Facebook</PdFormLabel>
              <PdHookFormInput
                name="facebook"
                placeholder="ex: www.facebook.com/brandname"
                onSubmitEditing={handleOnSubmit}
              />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Website</PdFormLabel>
              <PdHookFormInput name="website" placeholder="ex: www.brandname.com" onSubmitEditing={handleOnSubmit} />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Brand Video</PdFormLabel>
              <PdHookFormInput name="videoUrl" onSubmitEditing={handleOnSubmit} />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>About Me</PdFormLabel>
              <PdHookFormInput
                name="aboutMe"
                multiline
                numberOfLines={12}
                placeholder="Write a brief bio about your business."
              />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Brand Ethos *</PdFormLabel>
              <PdHookFormCheckboxGroup name="brandEthos" style={styles.mt2} options={brandEthosOptions} />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>City Based Out of</PdFormLabel>
              <PdHookFormInput name="cityBased" placeholder="New York City" onSubmitEditing={handleOnSubmit} />
            </View>
            <PdStack
              direction={isMobile ? "vertical" : "horizontal"}
              layout={[1, 1, 1]}
              gap={3}
              style={styles.selectFormSection}
            >
              <View>
                <PdFormLabel>Opening Order Minimum</PdFormLabel>
                <View style={styles.pdInputGroup}>
                  <PdText style={styles.ml2}>$</PdText>
                  <PdHookFormInputFloat
                    name="openingMinimum"
                    style={[styles.flex1, styles.border0]}
                    onSubmitEditing={handleOnSubmit}
                  />
                </View>
              </View>
              <View>
                <PdFormLabel>Reorder Minimum</PdFormLabel>
                <View style={styles.pdInputGroup}>
                  <PdText style={styles.ml2}>$</PdText>
                  <PdHookFormInputFloat
                    name="reorderMinimum"
                    style={[styles.flex1, styles.border0]}
                    onSubmitEditing={handleOnSubmit}
                  />
                </View>
              </View>
              <View>
                <PdFormLabel>Shipping Lead Time *</PdFormLabel>
                <PdHookFormSelect
                  name="shippingLeadTime"
                  rules={{ required: "Required" }}
                  options={shippingLeadTimeOptions}
                  isClearable={false}
                  onSubmitEditing={handleOnSubmit}
                  direction="top"
                />
                <PdHookFormErrorMessage name="shippingLeadTime" />
              </View>
            </PdStack>
            <View style={styles.mb3}>
              <View style={styles.formLabelLinkContainer}>
                <PdFormLabel>Default Shipping Percent</PdFormLabel>
                {rEstimatingShippingUrl && (
                  <TouchableOpacity
                    style={styles.formLabelLink}
                    onPress={() => openUrlInNewTab(rEstimatingShippingUrl)}
                  >
                    <PdTruck style={styles.formLabelLinkIcon} />
                    <PdText style={styles.formLabelLinkLabel}>Estimate Your shipping</PdText>
                  </TouchableOpacity>
                )}
              </View>
              <View style={styles.pdInputGroup}>
                <PdHookFormInputPercentage
                  style={[styles.flex1, styles.border0]}
                  name="defaultShipPercent"
                  onSubmitEditing={handleOnSubmit}
                />
                <PdText style={styles.mr2}>%</PdText>
              </View>
              <PdText variant="body-xs" style={styles.helperText}>
                Default shipping will apply to new retailers you connect with.
              </PdText>
              <PdText variant="body-xs" style={styles.helperText}>
                You can change the shipping on a per retailer basis once you and the retailer connect.
              </PdText>
            </View>
            <View>
              <PdFormLabel>Link to Brand Assets Folder (ex: Dropbox, Google Drive)</PdFormLabel>
              <PdHookFormInput
                placeholder="www.dropbox.com/brandassets"
                name="assetsFolderUrl"
                onSubmitEditing={handleOnSubmit}
              />
            </View>
          </View>
          {!isMobile && <PdDivider isVertical />}
          <View style={isMobile ? {} : styles.flex1}>
            <View style={styles.mb3}>
              <PdFormLabel>Profile Image</PdFormLabel>
              <PdHookFormImagePicker
                name="avatar"
                style={styles.avatarImagePicker}
                mobileStyle={styles.mobileAvatarImagePicker}
                webStyle={webStyles.avatarImagePicker}
              />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Carousel Images (minimum of 6 images required)</PdFormLabel>
              <PdHookFormMultipleImagePicker
                name="carousel"
                rules={{
                  validate: (value: string[]) => (value.length < 6 ? "You need to select at least 6 images" : true),
                }}
                length={9}
              />
              <PdHookFormErrorMessage name="carousel" />
            </View>
            <View>
              <PdFormLabel>As seen in (Select up to 6)</PdFormLabel>
              <PdHookFormSelectMultiple
                name="pressPublications"
                placeholder="Select Press Publications"
                rules={{
                  validate: (value: string[]) => (value.length > 6 ? "You can select up to 6 press features" : true),
                }}
                options={pressPublicationOptions}
                direction="top"
              />
              <PdHookFormErrorMessage name="pressPublications" />
            </View>
          </View>
        </PdStack>
      </PdFormCard>
      <PdFormCard>
        <View style={styles.formSectionTitle}>
          <PdSectionBadge>
            <PdSectionBadgeLabel>4</PdSectionBadgeLabel>
          </PdSectionBadge>
          <PdText variant="fieldset" style={styles.ml2}>
            TMT Admin Abilites
          </PdText>
        </View>
        <PdDivider style={styles.formSectionDivider} />
        <View style={isMobile ? styles.tmtAdminAbilitiesContentMobile : styles.tmtAdminAbilitiesContent}>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Email</PdFormLabel>
            <PdText style={styles.mb3}>{user.email}</PdText>
            <PdButton
              onPress={handleOnPressChangeEmail}
              isDisabled={user.isDeactivated}
              size="sm"
              colorScheme="pink"
              style={styles.mrAuto}
            >
              <PdButtonLabel>Change Email</PdButtonLabel>
            </PdButton>
          </View>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.mb3}>
            <View>
              <PdFormLabel>Is this company exclusive?</PdFormLabel>
              <View style={styles.yesNoSwitchFieldContainer}>
                <PdText>No</PdText>
                <PdHookFormSwitch name="isExclusive" style={styles.yesNoSwitchField} />
                <PdText>Yes</PdText>
              </View>
            </View>
            <View>
              <PdFormLabel>Publish brand to retailers?</PdFormLabel>
              <View style={styles.yesNoSwitchFieldContainer}>
                <PdText>No</PdText>
                <PdHookFormSwitch name="isPublished" style={styles.yesNoSwitchField} />
                <PdText>Yes</PdText>
              </View>
            </View>
          </PdStack>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3}>
            <View>
              <PdFormLabel>Commission Fee</PdFormLabel>
              <View style={styles.pdInputGroup}>
                <PdHookFormInputPercentage
                  style={[styles.flex1, styles.border0]}
                  name="commissionFee"
                  onSubmitEditing={handleOnSubmit}
                />
                <PdText style={styles.mr2}>%</PdText>
              </View>
            </View>
            <View>
              <PdFormLabel>Service Fee</PdFormLabel>
              <View style={styles.pdInputGroup}>
                <PdHookFormInputPercentage
                  style={[styles.flex1, styles.border0]}
                  name="serviceFee"
                  onSubmitEditing={handleOnSubmit}
                />
                <PdText style={styles.mr2}>%</PdText>
              </View>
            </View>
          </PdStack>
        </View>
      </PdFormCard>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        isReversed={isMobile}
        style={isMobile ? styles.mobileButtonGroup : styles.webButtonGroup}
      >
        <PdButton variant="outline" onPress={handleOnPressViewMyStore}>
          <PdButtonLabel>View My Store</PdButtonLabel>
        </PdButton>
        <PdButton isLoading={isSubmitting} onPress={handleOnSubmit}>
          <PdButtonLabel>Save Changes</PdButtonLabel>
        </PdButton>
      </PdStack>
    </FormProvider>
  );
};

const P06_09_AdminBrandSettings: React.FC = () => {
  const [brandUser, setBrandUser] = React.useState<tUser | undefined>(undefined);
  const [brand, setBrand] = React.useState<tBrand | undefined>(undefined);
  const isFocused = useIsFocused();
  const route = useRoute<tAdminBrandSettingsParams>();

  useFocusEffect(
    React.useCallback(() => {
      let userUnsubscribe: (() => void) | undefined;
      let brandUnsubscribe: (() => void) | undefined;

      if (route?.params?.brandId) {
        userUnsubscribe = queryUser({ companyRefPath: `brands/${route?.params?.brandId}` }).onSnapshot((snapshot) => {
          setBrandUser(snapshot.docs[0]?.data());
        });
        brandUnsubscribe = documentBrand(route?.params?.brandId).onSnapshot((document) => {
          setBrand(document.data());
        });
      }

      return () => {
        userUnsubscribe?.();
        brandUnsubscribe?.();
      };
    }, [route?.params?.brandId]),
  );

  React.useEffect(() => {
    if (!isFocused) {
      setBrandUser(undefined);
      setBrand(undefined);
    }
  }, [isFocused]);

  if (!brandUser || !brand) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <ScrollView style={[styles.p6, styles.bgLightGray]}>
      <AdminBrandForm brand={brand} user={brandUser} />
    </ScrollView>
  );
};

export const P06_09 = React.memo(P06_09_AdminBrandSettings);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  avatarImagePicker: {
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: 999,
    height: 141,
    justifyContent: "center",
    marginTop: 1 * kStyleConst.kSpacing,
    width: 141,
  },
  bgLightGray: {
    backgroundColor: kColors.lightGray,
  },
  border0: {
    borderWidth: 0,
  },
  flex1: {
    flex: 1,
  },
  formLabelLink: {
    alignItems: "center",
    flexDirection: "row",
  },
  formLabelLinkContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  formLabelLinkIcon: {
    width: 18,
  },
  formLabelLinkLabel: {
    fontSize: 10,
    marginLeft: 1 * kStyleConst.kSpacing,
    textDecorationLine: "underline",
  },
  formSection: {
    marginBottom: kStyleConst.kSpacing * 3,
    width: "100%",
  },
  formSectionDivider: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 1 * kStyleConst.kSpacing,
  },
  formSectionTitle: {
    alignItems: "center",
    flexDirection: "row",
  },
  helperText: {
    color: kColors.gray500,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mobileAvatarImagePicker: {
    borderRadius: 999,
    height: 141,
    width: 141,
  },
  mobileButtonGroup: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt2: {
    marginTop: kStyleConst.kSpacing * 2,
  },
  p6: {
    padding: kStyleConst.kSpacing * 6,
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  selectFormSection: {
    marginBottom: kStyleConst.kSpacing * 3,
    width: "100%",
  },
  tmtAdminAbilitiesContent: {
    width: "50%",
  },
  tmtAdminAbilitiesContentMobile: {
    width: "100%",
  },
  webButtonGroup: {
    justifyContent: "space-between",
    marginTop: 4 * kStyleConst.kSpacing,
  },
  yesNoSwitchField: {
    marginHorizontal: 1 * kStyleConst.kSpacing,
    transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }],
  },
  yesNoSwitchFieldContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  zIndex1: {
    zIndex: 1,
  },
  zIndex2: {
    zIndex: 2,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  sizeLg: {
    width: 141,
    height: 141,
  },
  roundFull: {
    borderRadius: 999,
  },
  avatarImagePicker: {
    width: 141,
    height: 141,
    borderRadius: 999,
  },
} as const;
